import { type UnparseConfig, unparse } from 'papaparse';

/**
 * One column a page offers for CSV download (APP-2436). Only columns defined in
 * a page's spec can reach a file, so a field left out of every spec can't be
 * exported by any picker setting. Keep sensitive fields out of specs entirely.
 */
export type CsvColumn<T, Ctx = void> = {
  /** Hidden in non-agent workspaces, where the value is always empty. */
  agentOnly?: boolean;
  /** Picker group. `default` columns are ticked when the dialog opens. */
  group: 'default' | 'details' | 'ids';
  /** The on-screen label. */
  header: string;
  /** Stable key for the picker and the saved selection. */
  id: string;
  value: (row: T, ctx: Ctx) => null | number | string | undefined;
};

/** A column in the 'default' (on-screen) group. */
export const csvColumn = <T, C = void>(
  id: string,
  header: string,
  value: CsvColumn<T, C>['value'],
  agentOnly?: boolean,
): CsvColumn<T, C> => ({ agentOnly, group: 'default', header, id, value });

// Not `escapeFormulae: true`: papaparse's own /^[=+\-@\t\r].*$/ has no `s`
// flag, so a value with a line break in it (a synopsis, say) went through
// unescaped. Only strings are tested, never numbers, so pass amounts as numbers
// or a negative one becomes text in Excel.
const OPTS: UnparseConfig = {
  escapeFormulae: /^[=+\-@\t\r]/,
  newline: '\r\n',
};

const BOM = '\uFEFF';

/** The header row, with a BOM so Excel reads the file as UTF-8. */
export const csvHeader = <T, C>(columns: CsvColumn<T, C>[]) =>
  BOM + unparse([columns.map((column) => column.header)], OPTS) + '\r\n';

/** One page of rows as CSV text, ending in CRLF so chunks concatenate. */
export const toCsvChunk = <T, C>(
  rows: T[],
  columns: CsvColumn<T, C>[],
  ctx: C,
) =>
  rows.length
    ? unparse(
        rows.map((row) =>
          columns.map((column) => column.value(row, ctx) ?? ''),
        ),
        OPTS,
      ) + '\r\n'
    : '';

const sydneyDate = new Intl.DateTimeFormat('en-CA', {
  day: '2-digit',
  month: '2-digit',
  timeZone: 'Australia/Sydney',
  year: 'numeric',
});

/** ISO `YYYY-MM-DD` in Sydney time, or `''` for a missing or invalid date. */
export const csvDate = (value?: null | string) => {
  const date = value ? new Date(value) : undefined;

  return date && !Number.isNaN(date.getTime()) ? sydneyDate.format(date) : '';
};

/** Several values in one cell. */
export const csvJoin = (values?: (null | string | undefined)[]) =>
  (values ?? []).filter(Boolean).join('; ');

/**
 * `acme-films_titles_2026-09-30.csv`. The workspace name is reduced to a plain
 * slug, so nothing a member typed can put path characters in the filename.
 */
export const csvFileName = (
  workspaceName: string | undefined,
  label: string,
  now = new Date(),
) => {
  const slug =
    (workspaceName ?? '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'workspace';

  return `${slug}_${label}_${sydneyDate.format(now)}.csv`;
};
